import * as THREE from 'three';

export type QuadrupedLegName = 'frontLeft' | 'frontRight' | 'hindLeft' | 'hindRight';

export interface QuadrupedLegRigDef {
  upper: string;
  lower: string;
  paw: string;
}

export interface QuadrupedRigDef {
  kind: 'quadruped';
  forward: '-z';
  boneAxis: '-y';
  spine: string;
  head: string;
  legs: Record<QuadrupedLegName, QuadrupedLegRigDef>;
  tail: string[];
  ears: [string, string];
}

export type AssetRigDef = QuadrupedRigDef;

/** Canonical node names required before a rigged asset may replace its fallback. */
export function rigNodeNames(rig: AssetRigDef): string[] {
  return [
    rig.spine,
    rig.head,
    ...Object.values(rig.legs).flatMap((leg) => [leg.upper, leg.lower, leg.paw]),
    ...rig.tail,
    ...rig.ears,
  ];
}

/**
 * A name is only safe as a binding when it exists exactly once. `getObjectByName`
 * silently choosing the first duplicate would make the imported result depend on
 * provider node order, which is not a stable rig contract.
 */
export function validateAssetRig(root: THREE.Object3D, rig: AssetRigDef): string[] {
  const counts = new Map<string, number>();
  const nodes = new Map<string, THREE.Object3D>();
  const skinnedBones = new Set<THREE.Bone>();
  let skinnedMeshes = 0;
  root.traverse((node) => {
    counts.set(node.name, (counts.get(node.name) ?? 0) + 1);
    nodes.set(node.name, node);
    if (node instanceof THREE.SkinnedMesh) {
      skinnedMeshes++;
      for (const bone of node.skeleton.bones) skinnedBones.add(bone);
    }
  });

  const problems: string[] = [];
  if (skinnedMeshes === 0) problems.push('model contains no skinned mesh');
  const required = rigNodeNames(rig);
  const repeatedContractNames = required.filter((name, index) => required.indexOf(name) !== index);
  for (const name of new Set(repeatedContractNames)) {
    problems.push(`rig contract repeats node name "${name}"`);
  }

  for (const name of new Set(required)) {
    const count = counts.get(name) ?? 0;
    const node = nodes.get(name);
    if (count === 0) problems.push(`missing rig node "${name}"`);
    if (count > 1) problems.push(`rig node "${name}" occurs ${count} times`);
    if (count === 1 && !(node instanceof THREE.Bone)) {
      problems.push(`rig node "${name}" is not a bone`);
    }
    if (count === 1 && node instanceof THREE.Bone && !skinnedBones.has(node)) {
      problems.push(`rig node "${name}" is not used by a skinned mesh`);
    }
  }
  return problems;
}

export function findRigNode(root: THREE.Object3D, name: string): THREE.Object3D | undefined {
  const node = root.getObjectByName(name);
  return node ?? undefined;
}
